import React from 'react';
import { Table, type TableProps } from 'antd';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { round } from 'lodash';
import { INotePurchaseReport } from '@/interfaces/report';
interface IProps {
  items: INotePurchaseReport[];
}

const NotePurchaseTable = ({ items }: IProps) => {
  dayjs.extend(relativeTime);
  const columns: TableProps<INotePurchaseReport>['columns'] = [
    {
      title: '#',
      dataIndex: 'idx',
      key: 'idx',
      align: 'center',
      width: 40,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Comercial',
      dataIndex: 'invNO',
      key: 'invNO',
      align: 'center',
      width: 140,
      className: 'font-bold',
    },
    {
      title: 'INV DATE',
      dataIndex: 'invDate',
      key: 'invDate',
      align: 'center',
      width: 60,
      className: 'font-bold',
      render: _ => formatDate(_),
    },
    {
      title: 'Amount',
      dataIndex: 'invAmount',
      key: 'invAmount',
      align: 'center',
      width: 60,
      className: 'font-bold',
      render: _ => formatNumber(_),
    },
    {
      title: 'PO#',
      dataIndex: 'poNo',
      key: 'poNo',
      align: 'center',
      width: 60,
      className: 'font-bold',
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      align: 'center',
      width: 60,
      className: 'font-bold',
    },
    {
      title: 'Product',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold text-blue-700',
      align: 'center',
      width: 120,
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      align: 'center',
      width: 100,
      className: 'font-bold',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Price',
      dataIndex: 'unitCost',
      key: 'unitCost',
      className: 'font-bold',
      width: 40,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Amount USD',
      dataIndex: 'amountUSD',
      key: 'amountUSD',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Ex Rate',
      dataIndex: 'rate',
      key: 'rate',
      className: 'font-bold text-yellow-700',
      width: 80,
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'Amount THB',
      dataIndex: 'amountTHB',
      key: 'amountTHB',
      className: 'font-bold text-yellow-700',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Freight',
      dataIndex: 'freightAmount',
      key: 'freightAmount',
      className: 'font-bold text-purple-700',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Freight/Unit',
      dataIndex: 'freight',
      key: 'freight',
      className: 'font-bold text-purple-700',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'Duty',
      dataIndex: 'dutyAmount',
      key: 'dutyAmount',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Duty/Unit',
      dataIndex: 'duty',
      key: 'duty',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'Vat',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_),
    },
    {
      title: 'Vat/Unit',
      dataIndex: 'vatCost',
      key: 'vatCost',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'Certificate No.',
      dataIndex: 'decNO',
      key: 'decNO',
      width: 100,
    },
    {
      title: 'Vendor INV',
      dataIndex: 'billingNo',
      key: 'billingNo',
      width: 90,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<INotePurchaseReport>
        columns={columns}
        criteria={undefined}
        data={{ data: items || [], totalRecords: items?.length }}
        loading={false}
        onTableChange={undefined}
        scrollLength={2200}
        scroll
        showPagination={false}
        summary={(pageData: INotePurchaseReport[]) => {
          let totalQuantity = 0;
          let totalFreight = 0;
          let totalDuty = 0;
          let totalUnitCost = 0;
          let totalUnitCostTHB = 0;
          let totalVat = 0;
          pageData.forEach(
            ({ quantity, duty, freight, vatCost, unitCost, unitCostTHB }) => {
              totalQuantity += quantity;
              totalFreight += round(quantity * freight, 4);
              totalUnitCost += round(quantity * unitCost, 4);
              totalUnitCostTHB += round(quantity * unitCostTHB, 4);
              totalDuty += round(quantity * duty, 4);
              totalVat += round(quantity * vatCost, 4);
            },
          );
          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell
                  index={0}
                  colSpan={9}
                  className="font-bold items-end"
                  align="right"
                ></Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(totalQuantity, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  className="font-bold items-end"
                  align="right"
                />
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(totalUnitCost, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  className="font-bold items-end"
                  align="right"
                />
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(totalUnitCostTHB, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(totalFreight, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  className="font-bold items-end"
                  align="right"
                />
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(totalDuty, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  className="font-bold items-end"
                  align="right"
                />
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(totalVat, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  className="font-bold items-end"
                  align="right"
                />
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                ></Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                ></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </div>
  );
};

export default NotePurchaseTable;
